/**
 * useCheckout — checkout orchestrator.
 * State: useCheckoutState  |  Order submission: _checkoutOrderService
 */
import axios from "axios";
import { useRef } from "react";
import { toast } from "sonner";
import { useCheckoutState } from "./useCheckoutState";
import { submitOrder as _submitOrder } from "./_checkoutOrderService";
import { applySmartPricing } from "@/utils/smartPricingUtils";
import { trackPixelEvent } from "@/hooks/useAnalytics";
import { trackStartCheckout } from "@/utils/tracking";
import { bcTrack } from "@/utils/behavioralCommerce";
import { useCheckoutTracking } from "./useCheckoutTracking";

export function useCheckout({
  restaurant, restaurantId, isTableOrder, isOpen, schedulingMode,
  tablePayAtEnd, tablePaymentEnabled, paymentAtTableEnabled, tableNumber,
  tableSession, refreshTableSession,
  cart, setCart,
  setCheckoutSuggestions, trackUpsell,
  API, paidOrderId, isPaid, cancelledOrderId, influencerCode,
  waPayReturn, waPayCancel,
  smartPricing, categories = [],
  fbclid = "", fbc = "", fbp = "", utm_source = "", utm_medium = "", utm_campaign = "", utm_content = "", utm_term = "",
  authToken = "",
}) {
  const s = useCheckoutState({
    restaurant, restaurantId, API, paidOrderId, isPaid, cancelledOrderId,
    waPayReturn, waPayCancel,
  });
  const { trackEvent: trackCheckout } = useCheckoutTracking(restaurantId, authToken);
  const reservationSubmitInFlightRef = useRef(false);

  const submitOrder = () => {
    trackCheckout("payment_started", {
      item_count: cart.reduce((sum, i) => sum + i.quantity, 0),
      cart_value_minor: Math.round((cart.reduce((sum, i) => sum + i.price * i.quantity, 0)) * 100),
      currency: restaurant?.currency || "EUR",
      payment_method: s.paymentMethod,
    });
    const categoryMap = {};
    for (const cat of (categories || [])) {
      if (cat.id) categoryMap[cat.id] = cat.name || "";
    }
    const { discountAmount: smartDiscountAmount, subtotal: smartSubtotal } =
      applySmartPricing(cart, smartPricing, categoryMap);
    return _submitOrder({
    cart, isTableOrder, orderType: s.orderType,
    deliveryAddress: s.deliveryAddress, addressNumber: s.addressNumber,
    addressComplement: s.addressComplement, addressBarrio: s.addressBarrio,
    customerName: s.customerName, customerPhone: s.customerPhone,
    fullAddress: s.fullAddress, deliveryLat: s.deliveryLat, deliveryLng: s.deliveryLng,
    scheduleDate: s.scheduleDate, scheduleTime: s.scheduleTime,
    schedulingMode, isOpen, paymentMethod: s.paymentMethod,
    changeFor: s.changeFor, orderNotes: s.orderNotes,
    couponCode: s.couponCode, loyaltyReward: s.loyaltyReward, influencerCode,
    tableNumber, paymentAtTableEnabled, tablePayAtEnd, tablePaymentEnabled,
    tableSession, restaurant, restaurantId, API, fmt: s.fmt,
    setSubmitting: s.setSubmitting, setOrderConfirmed: s.setOrderConfirmed,
    setConfirmedCart: s.setConfirmedCart, setCart,
    setShowCheckout: s.setShowCheckout, setCheckoutStep: s.setCheckoutStep,
    setOrderNotes: s.setOrderNotes, refreshTableSession, saveCustomerLocal: s.saveCustomerLocal,
    smartPricing, smartDiscountAmount, smartSubtotal,
    fbclid, fbc, fbp, utm_source, utm_medium, utm_campaign, utm_content, utm_term,
  });
  };

  const openCheckout = () => {
    trackCheckout("checkout_started", {
      item_count: cart.reduce((sum, i) => sum + i.quantity, 0),
      cart_value_minor: Math.round((cart.reduce((sum, i) => sum + i.price * i.quantity, 0)) * 100),
      currency: restaurant?.currency || "EUR",
    });
    const forceSchedule = schedulingMode === "forced" || (!isOpen && !isTableOrder);
    if (forceSchedule || schedulingMode !== "disabled") {
      const dates = s.getAvailableDates();
      if (dates.length > 0 && s.scheduleDate === "asap" && !isOpen) {
        s.setScheduleDate(dates[0].value);
        const slots = s.getTimeSlots(dates[0].value);
        if (slots.length > 0) s.setScheduleTime(slots[0]);
      }
    }
    if (schedulingMode === "disabled") s.setScheduleDate("asap");
    s.setCheckoutStep(isTableOrder ? 2 : 1);
    s.setShowCheckout(true);

    if (cart.length > 0) {
      const cartValue = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
      const checkoutEventId = crypto.randomUUID();
      trackPixelEvent("InitiateCheckout", {
        value: cartValue,
        currency: restaurant?.currency || "EUR",
        num_items: cart.reduce((sum, i) => sum + i.quantity, 0),
        content_ids: cart.map(i => i.id),
        content_type: "product",
      }, { eventID: checkoutEventId });
      trackStartCheckout({ cart_value: cartValue, metadata: { event_id: checkoutEventId, num_items: cart.reduce((sum, i) => sum + i.quantity, 0) } });
      bcTrack("checkout_start", { cart_value: cartValue, metadata: { num_items: cart.reduce((sum, i) => sum + i.quantity, 0) } });
      const randomCartItem = cart[Math.floor(Math.random() * cart.length)];
      const cartIds = cart.map(i => i.id);
      axios.get(`${API}/public/suggestions/${restaurantId}?product_id=${randomCartItem.id}`)
        .then(res => {
          const sugs = (res.data || []).filter(s => !cartIds.includes(s.id));
          setCheckoutSuggestions(sugs.slice(0, 3));
          sugs.slice(0, 3).forEach(s => trackUpsell("shown", "checkout", s));
        }).catch(() => {});
    }
  };

  const submitReservation = async (finalData) => {
    // P0.7: Synchronous lock to prevent double submissions
    if (reservationSubmitInFlightRef.current) return;
    reservationSubmitInFlightRef.current = true;

    // Use passed finalData if available (from ReservationDialog with policy_accepted);
    // otherwise fall back to state (for backward compatibility)
    const dataToSubmit = finalData || s.reservationData;
    const { customer_name, customer_phone, date, time, policy_accepted } = dataToSubmit;

    if (!customer_name || !customer_phone || !date || !time) {
      toast.error("Preencha nome, telefone, data e hora");
      reservationSubmitInFlightRef.current = false;
      return;
    }
    if (!policy_accepted) {
      toast.error("Você deve aceitar a política de reservas");
      reservationSubmitInFlightRef.current = false;
      return;
    }

    s.setReservationSubmitting(true);
    s.setReservationSlotAlternatives([]);
    try {
      const response = await axios.post(`${API}/public/reservation`, {
        ...dataToSubmit,
        restaurant_id: restaurant?.id || restaurantId,
        guests: Number(dataToSubmit.guests),
      });
      s.setReservationConfirmed(true);
      toast.success(response.data?.message || "Reserva enviada! Confirmaremos em breve.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.reason === "slot_full" && detail?.available_slots?.length > 0) {
        s.setReservationSlotAlternatives(detail.available_slots);
        toast.warning(detail.message || "Horário indisponível");
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "Erro ao enviar reserva");
        toast.error(msg);
      }
    } finally {
      s.setReservationSubmitting(false);
      reservationSubmitInFlightRef.current = false;
    }
  };

  const selectReservationAlternative = (time) => {
    s.setReservationData(prev => ({ ...prev, time }));
    s.setReservationSlotAlternatives([]);
  };

  return {
    ...s,
    submitOrder, openCheckout, submitReservation, selectReservationAlternative,
  };
}
